import AsyncStorage from '@react-native-async-storage/async-storage';

/**
 * A household invite code the visitor opened before they had an account.
 *
 * `/join/{code}` sends a signed-out visitor to `/auth` to register, and that
 * round trip used to lose the code: nothing carried it and nothing stored it,
 * so every invitee who did not already have an account landed on the home
 * screen as a brand-new solo user while the inviter's Settings went on
 * promising "invitation pending" for 24 hours. Held across the auth round trip
 * and replayed once the account exists.
 *
 * The same shape as `pendingImport`, with one deliberate difference: reading
 * does NOT consume. The code has to survive from `/join` through `/auth` and
 * back to `/join`, so it is cleared on arrival — when the join lands, when the
 * invitee backs out, or when the invite turns out to be dead — not on read.
 *
 * A raw storage key rather than redux-persist on purpose: Google sign-in can
 * fall back to `signInWithRedirect` (authService.ts), which is a full page
 * navigation, so in-memory state and URL params are both gone by the time the
 * user comes back.
 */

const KEY = 'paprika_pending_invite_code';

/**
 * Matches the 24 h invite TTL in householdService. A code held any longer than
 * the invite itself can only ever resolve to "this invitation has expired",
 * which is a worse landing than the home screen.
 */
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

interface StoredPendingInvite {
  code: string;
  savedAt: number;
}

export async function setPendingInvite(code: string): Promise<void> {
  try {
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return;
    const payload: StoredPendingInvite = { code: trimmed, savedAt: Date.now() };
    await AsyncStorage.setItem(KEY, JSON.stringify(payload));
  } catch {
    // A lost hand-off costs the user a second tap on the link; a throw on the
    // auth path costs them the account. Fail quietly.
  }
}

/**
 * Read without consuming.
 *
 * Consuming here would drop the code at the `/auth` redirect, which is the one
 * moment it still has work to do.
 */
export async function readPendingInvite(): Promise<string | null> {
  try {
    const raw = await AsyncStorage.getItem(KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as StoredPendingInvite;
    if (!parsed?.code || typeof parsed.savedAt !== 'number') return null;
    if (Date.now() - parsed.savedAt > MAX_AGE_MS) return null;
    return parsed.code;
  } catch {
    return null;
  }
}

export async function clearPendingInvite(): Promise<void> {
  try {
    await AsyncStorage.removeItem(KEY);
  } catch {
    // Nothing to recover; the age check above bounds a stale entry anyway.
  }
}

/**
 * Where to send someone the moment they finish authenticating.
 *
 * Pure so it is testable: none of the auth screen is reachable from jest —
 * this repo stubs `react-native` rather than rendering it.
 *
 * The `next` param is the fast path, used when nothing reloaded. The stash is
 * the durable one. The param is validated rather than trusted: it ends up in
 * `router.replace`, and the only destination this hand-off is allowed to name
 * is a join screen.
 */
const JOIN_ROUTE = /^\/join\/([a-z0-9]{6})$/i;

/** The invite alphabet, minus the ambiguous 0/O and 1/I/L. */
const INVITE_CODE = /^[ABCDEFGHJKMNPQRSTUVWXYZ23456789]{6}$/;

/** `/join/{CODE}` if the code is a real one, else null. */
const joinRouteFor = (raw: string | null | undefined): string | null => {
  const code = raw?.trim().toUpperCase() ?? '';
  return INVITE_CODE.test(code) ? `/join/${code}` : null;
};

export function resolvePostAuthRoute(
  nextParam: string | string[] | undefined,
  stashedCode: string | null
): string | null {
  const next = Array.isArray(nextParam) ? nextParam[0] : nextParam;
  const matched = typeof next === 'string' ? JOIN_ROUTE.exec(next.trim()) : null;

  // A malformed param falls through to the stash rather than giving up: the
  // stash is the more trustworthy of the two.
  return joinRouteFor(matched?.[1]) ?? joinRouteFor(stashedCode);
}
